// "מיניסייטים של המשרד" — what each colleague in the viewer's office sent this customer, and what
// the customer opened there. Read only: a colleague's page is theirs to change.
//
// Two levels, decided by the server: an office admin, or a member allowed to look through the
// office's customers, gets each page in full (the link, the listings, what the customer opened);
// everybody else in the office is told only that the colleague has a page and how many listings are
// on it (`access: "count"`) — the face, the name and the number, nothing to open.
//
// Asked for by an office (URBANIX, Trello #224): every agent keeps their own minisite for a customer,
// and the office — the manager and the agents — wants to see the others' before sending, so that an
// agent does not send a listing another agent already sent, or one the customer already opened.
// Keyed by the customer's PHONE (the server's `POST /contacts/office-minisites`), so it shows the
// same whether the office keeps personal books or a pooled one.
//
// Mounted in two places: the matches tab's "המשרד" pill (page and CRM side panel alike), and the
// side panel's colleague-only branch, where a manager opens a customer they do not hold.
import { useState } from "react";
import { Avatar, AvatarGroup, Box, Button, ButtonBase, Skeleton, Stack, Tooltip, Typography } from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import OpenInNewRoundedIcon from "@material-ui/icons/OpenInNewRounded";
import Label from "components/Label";
import useOrgDirectory from "hooks/useOrgDirectory";
import { stringAvatar } from "utils/contacts/display";
import type { FirestoreListingHit } from "utils/listings/fetchListings";
import CrmMemberAvatar from "../../../../crm/org/CrmMemberAvatar";
import ListingQuickViewDialog from "./ListingQuickViewDialog";
import { buildListingAddressLine, buildListingMetaLine, formatPriceShort, formatRelativeDayTime } from "./matchesDisplay";
import { buildMinisiteUrl } from "./matchesShare";
import { useOfficeMinisiteListings, useOfficeMinisites } from "./officeMinisitesData";
import {
  OFFICE_MINISITE_COLLAPSED_ROWS,
  officeListingTags,
  officeMinisitePresenceLine,
  officeMinisiteRealtorName,
  officeMinisitesCaption,
  officeMinisiteSummaryLine,
  officeMinisiteTotals
} from "./officeMinisitesModel";
import type { REST_schema } from "../../../../../../@types/sharedSchema";

type OfficeMinisitesPanelProps = {
  /** The customer's phone — the contact id. */
  contactId: string;
  /** The listings on the viewer's OWN page for this customer: those are marked on a colleague's page. */
  ownListingIds?: readonly string[];
  /**
   * The side panel's colleague branch has no pill to name the section, so it carries its own title
   * — and renders nothing at all until there is something to show.
   */
  standalone?: boolean;
};

export default function OfficeMinisitesPanel({
  contactId,
  ownListingIds = [],
  standalone = false
}: OfficeMinisitesPanelProps): JSX.Element | null {
  const office = useOfficeMinisites(contactId);
  const [openListing, setOpenListing] = useState<FirestoreListingHit | null>(null);

  if (office.status === "off") return null;
  if (standalone && office.status !== "ready") return null;
  if (office.status === "ready" && office.minisites.length === 0) return null;

  const mine = new Set(ownListingIds);

  return (
    <Stack spacing={1.5} sx={{ mt: standalone ? 0 : 2 }}>
      <Box>
        {standalone && <Typography variant="subtitle1">מיניסייטים של המשרד</Typography>}
        <Typography variant="caption" color="text.secondary">
          {officeMinisitesCaption(office.minisites)}
        </Typography>
      </Box>

      {office.status === "loading" && (
        <Stack spacing={1}>
          <Skeleton variant="rectangular" height={88} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={88} sx={{ borderRadius: 2 }} />
        </Stack>
      )}

      {office.status === "error" && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" color="text.secondary">
            לא הצלחנו לטעון את המיניסייטים של המשרד.
          </Typography>
          <Button size="small" onClick={office.refresh}>
            נסו שוב
          </Button>
        </Stack>
      )}

      {office.status === "ready" &&
        office.minisites.map((minisite) => (
          <OfficeMinisiteCard key={minisite.realtorId} minisite={minisite} mine={mine} onOpenListing={setOpenListing} />
        ))}

      <ListingQuickViewDialog open={Boolean(openListing)} listing={openListing} onClose={() => setOpenListing(null)} />
    </Stack>
  );
}

/** How many faces the "המשרד" pill shows before "+N". */
const PILL_FACES = 3;

/**
 * The faces of the colleagues who sent this customer a page — what the "המשרד" pill shows, so the
 * office's pages are visible before anybody opens them. Their names on hover.
 */
export function OfficeMinisitesAvatars({
  minisites
}: {
  minisites: readonly REST_schema.CrmOfficeMinisite[];
}): JSX.Element | null {
  const { getMember } = useOrgDirectory();
  if (minisites.length === 0) return null;
  const people = minisites.map((minisite) => {
    const member = getMember(minisite.realtorId);
    return {
      id: minisite.realtorId,
      name: officeMinisiteRealtorName(minisite, member?.displayName),
      avatarUrl: minisite.realtorAvatarUrl ?? member?.avatarUrl ?? null
    };
  });
  const shown = people.slice(0, PILL_FACES);
  const overflow = people.length - shown.length;
  return (
    <Tooltip title={`נשלח ללקוח גם ע״י ${people.map((person) => person.name).join(", ")}`}>
      <AvatarGroup
        sx={{
          "& .MuiAvatar-root": {
            width: 22,
            height: 22,
            fontSize: 10,
            border: (theme) => `2px solid ${theme.palette.background.paper}`
          }
        }}
      >
        {shown.map((person) => {
          const { sx: tint, children: initials } = stringAvatar(person.name);
          return (
            <Avatar key={person.id} src={person.avatarUrl ?? undefined} alt={person.name} sx={{ color: "common.white", ...tint }}>
              {initials}
            </Avatar>
          );
        })}
        {overflow > 0 && <Avatar>+{overflow}</Avatar>}
      </AvatarGroup>
    </Tooltip>
  );
}

function OfficeMinisiteCard({
  minisite,
  mine,
  onOpenListing
}: {
  minisite: REST_schema.CrmOfficeMinisite;
  mine: ReadonlySet<string>;
  onOpenListing: (listing: FirestoreListingHit) => void;
}): JSX.Element {
  const theme = useTheme();
  const { getMember } = useOrgDirectory();
  const member = getMember(minisite.realtorId);
  const name = officeMinisiteRealtorName(minisite, member?.displayName);
  const totals = officeMinisiteTotals(minisite);
  // A page the viewer is only told about has no link and no listings, and nothing is fetched for it.
  const full = minisite.access === "full" && Boolean(minisite.minisiteId);
  const [expanded, setExpanded] = useState(false);
  const { listings: hits, loading } = useOfficeMinisiteListings(minisite, full);
  const rows = expanded ? minisite.listings : minisite.listings.slice(0, OFFICE_MINISITE_COLLAPSED_ROWS);
  const hidden = minisite.listings.length - rows.length;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: 1.5,
        bgcolor: "background.paper"
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <CrmMemberAvatar name={name} avatarUrl={minisite.realtorAvatarUrl ?? member?.avatarUrl} size={36} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" noWrap>
            {`המיניסייט של ${name}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" noWrap>
            {full ? officeMinisiteSummaryLine(totals) : officeMinisitePresenceLine(minisite)}
            {full && minisite.lastPageViewAt ? ` · ביקור אחרון ${formatRelativeDayTime(new Date(minisite.lastPageViewAt))}` : ""}
          </Typography>
        </Box>
        {full && (
          <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewRoundedIcon fontSize="small" />}
            href={buildMinisiteUrl(minisite.minisiteId as string)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ flexShrink: 0 }}
          >
            פתיחה
          </Button>
        )}
      </Stack>

      <Stack spacing={0.5} sx={{ mt: rows.length > 0 ? 1 : 0 }}>
        {rows.map((listing) => {
          const hit = hits.get(listing.listingId);
          const imageUrl = hit?.coverImageUrl || hit?.imageUrls?.[0];
          const detail = hit ? [formatPriceShort(hit.price), buildListingMetaLine(hit)].filter(Boolean).join(" · ") : "";
          return (
            <ButtonBase
              key={listing.listingId}
              disabled={!hit}
              onClick={() => hit && onOpenListing(hit)}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                textAlign: "start",
                borderRadius: 1.5,
                p: 0.75,
                gap: 1.25,
                "&:hover": { bgcolor: alpha(theme.palette.grey[500], 0.08) }
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  flexShrink: 0,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.grey[500], 0.16),
                  backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
                  backgroundSize: "cover",
                  backgroundPosition: "center"
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                {hit ? (
                  <Typography variant="body2" noWrap>
                    {buildListingAddressLine(hit)}
                  </Typography>
                ) : loading ? (
                  <Skeleton width="60%" />
                ) : (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    נכס שכבר לא מפורסם
                  </Typography>
                )}
                {detail && (
                  <Typography variant="caption" color="text.secondary" component="div" noWrap>
                    {detail}
                  </Typography>
                )}
                <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5, mt: 0.5 }}>
                  {officeListingTags(listing, { alsoOnMine: mine.has(listing.listingId) }).map((tag) => (
                    <Label key={tag.key} color={tag.color}>
                      {tag.label}
                    </Label>
                  ))}
                </Stack>
              </Box>
              {listing.lastViewedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {formatRelativeDayTime(new Date(listing.lastViewedAt))}
                </Typography>
              )}
            </ButtonBase>
          );
        })}
      </Stack>

      {(hidden > 0 || expanded) && minisite.listings.length > OFFICE_MINISITE_COLLAPSED_ROWS && (
        <Button size="small" onClick={() => setExpanded((value) => !value)} sx={{ mt: 0.5 }}>
          {expanded ? "הצגת פחות" : `הצגת כל ${minisite.listings.length} הנכסים`}
        </Button>
      )}
    </Box>
  );
}
